import ReactECharts from 'echarts-for-react';
import studioData from '../data/studioMethodology.json';

/**
 * MethodologyLoopChart — the Human Input -> AI Produce -> Human Review -> Ship
 * loop (Fig 1). This is a structural diagram of Playano's own stated
 * methodology (category (c) under the no-invented-data rule — the model's own
 * definition, not a measurement), transcribed from the real landing-page loop
 * diagram (Playano_Ed_HP_4/public/index.html, "Our Methodology" section).
 * Rendered as a 4-node directed graph so the cycle (Ship -> back to AI Produce)
 * is visible, matching the source diagram's feedback arc.
 */

type LoopStep = { step: number; label: string; sub: string; detail: string; color: string };

export const MethodologyLoopChart = ({ height = 340 }: { height?: number }) => {
  const loop = (studioData as unknown as { loop: LoopStep[] }).loop;

  const nodes = loop.map((s, i) => ({
    name: s.label,
    x: 80 + i * 130,
    y: 80,
    symbolSize: i === 1 ? 56 : 44,
    itemStyle: { color: s.color, borderColor: '#111', borderWidth: 2 },
    label: { show: true, position: 'bottom', color: '#e5e5e5', fontWeight: 700, fontSize: 13, distance: 12 },
  }));

  const links = [
    { source: 'Human Input', target: 'AI Produce', lineStyle: { color: '#3b82f6', width: 3, curveness: 0 } },
    { source: 'AI Produce', target: 'Human Review', lineStyle: { color: '#06b6d4', width: 3, curveness: 0 } },
    { source: 'Human Review', target: 'Ship', lineStyle: { color: '#3b82f6', width: 3, curveness: 0 } },
    { source: 'Human Review', target: 'AI Produce', lineStyle: { color: '#10b981', width: 2, curveness: -0.4, type: 'dashed' } },
  ];

  const option = {
    backgroundColor: '#171717',
    tooltip: {
      trigger: 'item',
      formatter: (p: { dataType: string; data: { name?: string } }) =>
        p.dataType === 'node'
          ? `<b>${p.data.name}</b><br/>${loop.find((s) => s.label === p.data.name)?.detail ?? ''}`
          : '',
      backgroundColor: '#0a0a0a',
      borderColor: '#333',
      textStyle: { color: '#f5f5f5', fontSize: 13 },
    },
    series: [
      {
        type: 'graph',
        layout: 'none',
        coordinateSystem: undefined,
        symbol: 'circle',
        data: nodes,
        links,
        edgeSymbol: ['none', 'arrow'],
        edgeSymbolSize: 10,
        lineStyle: { curveness: 0 },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-[#333] bg-[#171717] p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-gray-400 text-center px-4">
        Figure 1. The Playano loop: humans provide input and review, AI produces and iterates. The
        dashed arc is the feedback cycle — every human correction trains the next AI Produce pass.
      </p>
    </div>
  );
};
